import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import ParallaxBanner from '../components/ParallaxBanner';
import contentService from '../services/contentService';
import featureFlags from '../utils/featureFlags';
import useContentUpdates from '../hooks/useContentUpdates';
import { orderService } from '../services/orderService';
import { formatOrderPrice } from '../utils/apiHelpers';
import { useAuth } from '../hooks/useAuth';

const PAGE_SIZE = 5; // Orders per page

function Orders() {
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(0); // API uses 0-indexed pages
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  // Fetch orders with pagination
  const fetchOrders = useCallback(async (page = 0) => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await orderService.getOrders({
        page: page,
        size: PAGE_SIZE
      });

      console.log('[Orders] API response:', response);

      if (response.success) {
        // Handle different response formats:
        // - response.data could be an array directly
        // - response.data could be paginated: { content: [], totalPages: X }
        // - response.data could be nested: { orders: [] }
        let ordersData = response.data;

        if (Array.isArray(ordersData)) {
          setOrders(ordersData);
          setTotalPages(1);
          setTotalElements(ordersData.length);
        } else if (ordersData?.content && Array.isArray(ordersData.content)) {
          setOrders(ordersData.content);
          setTotalPages(ordersData.totalPages || 1);
          setTotalElements(ordersData.totalElements || ordersData.content.length);
          setCurrentPage(ordersData.page || page);
        } else if (ordersData?.orders && Array.isArray(ordersData.orders)) {
          setOrders(ordersData.orders);
          setTotalPages(1);
          setTotalElements(ordersData.orders.length);
        } else {
          console.warn('[Orders] Unexpected data format:', ordersData);
          setOrders([]);
        }
      } else {
        throw new Error(response.error?.message || 'Failed to load orders');
      }
    } catch (err) {
      console.error('[Orders] Error fetching orders:', err);
      setError(err.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Initial fetch and refetch when authentication changes
  useEffect(() => {
    fetchOrders(0);
    setCurrentPage(0);
  }, [fetchOrders]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
      fetchOrders(newPage);
      // Scroll to top of orders section
      window.scrollTo({ top: 300, behavior: 'smooth' });
    }
  }, [totalPages, fetchOrders]);

  // Initialize scripts
  useEffect(() => {
    const handleWindowLoad = () => {
      if (window.$ && window.$('.loader1').length > 0) {
        window.$(".loader1").fadeOut(800);
        window.$('.side-menu').removeClass('hidden');
      }
    };

    if (document.readyState === 'complete') {
      handleWindowLoad();
    } else {
      window.addEventListener('load', handleWindowLoad);
    }

    if (window.WOW) {
      new window.WOW().init();
    }

    return () => {
      window.removeEventListener('load', handleWindowLoad);
    };
  }, []);

  // Get payment status badge color
  const getPaymentStatusBadgeClass = (paymentStatus) => {
    switch (paymentStatus?.toUpperCase()) {
      case 'PAID':
        return 'badge-success';
      case 'PENDING':
        return 'badge-warning';
      case 'FAILED':
      case 'CANCELLED':
      case 'REFUNDED':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  };


  const [backgroundUrl, setBackgroundUrl] = useState(null);

  const fetchBackground = useCallback(async () => {
    if (!featureFlags.useDynamicBackgrounds()) {
      return;
    }
    try {
      const result = await contentService.getPageBackground('product-listing');
      if (result.success && result.imageUrl) {
        setBackgroundUrl(result.imageUrl);
      }
    } catch (err) {
      console.error('[Orders] Error fetching background:', err);
    }
  }, []);

  useEffect(() => {
    fetchBackground();
  }, [fetchBackground]);

  useContentUpdates({
    onPageBackgroundUpdated: (page) => {
      if (page === 'product-listing') {
        fetchBackground();
      }
    }
  });

  return (
    <Layout>
      {/* Parallax Banner */}
      {backgroundUrl && (
        <ParallaxBanner
          imageUrl={backgroundUrl}
          speed={0.6}
          offset={0}
          className="paralax-section-slide-data"
        />
      )}

      {/* Orders Section */}
      <div className="about_content">
        <div className="container">
          <div className="row">
            <div className="col-12 text-center text-lg-left wow slideInUp" data-wow-duration="2s">
              <h1 className="heading">My Orders</h1>
              <p className="para_text w-100 w-lg-75">View and track your order history.</p>
            </div>
          </div>

          {!isAuthenticated ? (
            <div className="row mt-5">
              <div className="col-12 text-center py-5">
                <i className="fas fa-user-lock mb-4" style={{ fontSize: '60px', color: '#6c757d' }}></i>
                <h3 className="mb-3">Please Log In</h3>
                <p className="text-muted mb-4">You need to be logged in to view your orders.</p>
                <Link to="/login" className="btn pink-gradient-btn-into-black">
                  Log In
                </Link>
              </div>
            </div>
          ) : loading ? (
            <div className="row mt-5">
              <div className="col-12 text-center py-5">
                <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading orders...</p>
              </div>
            </div>
          ) : error ? (
            <div className="row mt-5">
              <div className="col-12 text-center py-5">
                <div className="alert alert-danger">
                  <i className="fas fa-exclamation-triangle mr-2"></i>
                  {error}
                </div>
                <button
                  className="btn pink-gradient-btn-into-black"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </button>
              </div>
            </div>
          ) : orders.length === 0 ? (
            <div className="row mt-5">
              <div className="col-12 text-center py-5">
                <i className="fas fa-shopping-bag mb-4" style={{ fontSize: '60px', color: '#6c757d' }}></i>
                <h3 className="mb-3">No Orders Yet</h3>
                <p className="text-muted mb-4">You haven't placed any orders yet.</p>
                <Link to="/product-listing" className="btn pink-gradient-btn-into-black">
                  Start Shopping
                </Link>
              </div>
            </div>
          ) : (
            <div className="row mt-5">
              <div className="col-12">
                {/* Orders count info */}
                {totalElements > 0 && (
                  <p className="text-muted mb-4">
                    Showing {currentPage * PAGE_SIZE + 1} - {Math.min((currentPage + 1) * PAGE_SIZE, totalElements)} of {totalElements} orders
                  </p>
                )}

                {orders.map((order) => (
                  <div key={order.id} className="card mb-4 wow fadeIn" data-wow-duration="1s">
                    <div className="card-header d-flex justify-content-between align-items-center">
                      <div>
                        <strong>Order #{order.orderNumber}</strong>
                        <span className="text-muted ml-3">
                          {new Date(order.createdAt).toLocaleDateString('en-GB', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                      {/* Payment Status Badge */}
                      <span className={`badge ${getPaymentStatusBadgeClass(order.paymentStatus)}`} style={{
                        padding: '8px 12px',
                        fontSize: '12px',
                        textTransform: 'uppercase'
                      }}>
                        {order.paymentStatus || 'PENDING'}
                      </span>
                    </div>
                    <div className="card-body">
                      {/* Order Items Preview */}
                      <div className="order-items mb-3">
                        {order.items?.slice(0, 3).map((item, index) => (
                          <div key={item.id || index} className="d-flex align-items-center mb-2">
                            {item.product?.images?.[0]?.url && (
                              <img
                                src={item.product.images[0].url}
                                alt={item.productName || item.product?.name}
                                style={{
                                  width: '50px',
                                  height: '50px',
                                  objectFit: 'cover',
                                  marginRight: '10px',
                                  borderRadius: '4px'
                                }}
                              />
                            )}
                            <div>
                              <span>{item.productName || item.product?.name}</span>
                              <span className="text-muted ml-2">x{item.quantity}</span>
                            </div>
                          </div>
                        ))}
                        {order.items?.length > 3 && (
                          <p className="text-muted small mb-0">
                            +{order.items.length - 3} more item(s)
                          </p>
                        )}
                      </div>

                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>Total: </strong>
                          <span className="text-primary" style={{ fontSize: '18px' }}>
                            {formatOrderPrice(order.summary?.total || order.total || 0)}
                          </span>
                        </div>
                        <Link
                          to={`/order-confirmation/${order.id}`}
                          className="btn btn-outline-primary btn-sm"
                        >
                          View Details
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <nav aria-label="Orders pagination" className="mt-4 mb-5">
                    <ul className="pagination justify-content-center">
                      {/* Previous Button */}
                      <li className={`page-item ${currentPage === 0 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 0}
                          aria-label="Previous"
                        >
                          <i className="fas fa-chevron-left"></i>
                        </button>
                      </li>

                      {/* Page Numbers */}
                      {[...Array(totalPages)].map((_, index) => (
                        <li key={index} className={`page-item ${currentPage === index ? 'active' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(index)}
                          >
                            {index + 1}
                          </button>
                        </li>
                      ))}

                      {/* Next Button */}
                      <li className={`page-item ${currentPage === totalPages - 1 ? 'disabled' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages - 1}
                          aria-label="Next"
                        >
                          <i className="fas fa-chevron-right"></i>
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Orders;
